import { camelize, nuveiTax1, nuveiTax2 } from 'utils/utils';
import { HandleTotalPriceProps } from './types';
import { getData } from 'services/api';
import { useQuery } from '@tanstack/react-query';
import { TicketDetailsParams } from 'features/TicketDetails/types';
import { useParams } from 'react-router-dom';
import { TaxesType } from 'features/BoxOffice/OrderSummary/types';
import { EventData } from 'features/common/types';
import { round } from 'features/BoxOffice/OrderSummary/utils';

export const stripeFee1 = 0.0294;
export const stripeFee2 = 0.3;
export const minStripeProcessingAmount = 0.5;

export const handleTotalPrice = ({ taxesData, watch }: HandleTotalPriceProps) => {
	const { taxes, partner_royalties } = taxesData;

	const initialPrice = Number(watch('initialPrice'));
	const buyerAbsorbPaymentFee = watch('buyerAbsorbPaymentFee') === 'true';
	const buyerAbsorbTixologistFee = watch('buyerAbsorbTixologistFee') === 'true';

	const initialPriceWithTaxes = initialPrice + initialPrice * taxes;
	const totalWithFees =
		initialPrice +
		initialPrice * partner_royalties.tixologi_primary_royalty +
		partner_royalties.tixologi_fixed_primary_royalty;
	const totalFeesWithTaxes = totalWithFees + totalWithFees * taxes;

	if (!buyerAbsorbPaymentFee && !buyerAbsorbTixologistFee) return initialPriceWithTaxes;
	else if (buyerAbsorbPaymentFee && !buyerAbsorbTixologistFee) {
		return initialPriceWithTaxes + initialPriceWithTaxes * nuveiTax1 + nuveiTax2;
	} else if (!buyerAbsorbPaymentFee && buyerAbsorbTixologistFee) {
		return totalFeesWithTaxes;
	} else {
		return totalFeesWithTaxes + totalFeesWithTaxes * nuveiTax1 + nuveiTax2;
	}
};

export const useGetTaxes = () => {
	const { ticketId } = useParams<TicketDetailsParams>();

	const { data: taxes } = useQuery(
		['getTaxes', ticketId],
		() => getData(`public/ticketing/ticket_types/taxes/${ticketId}`),
		{ enabled: !!ticketId }
	);

	return { taxes: camelize(taxes) as TaxesType };
};

/**
 * Calculates the base ticket price that will result in a specific final price after all fees and taxes.
 * This uses algebraic reverse calculation to ensure the checkout flow produces the exact desired final price.
 *
 * Checkout calculation flow:
 * 1. customFees = basePrice × customFeeRate% + flatCustomFees
 * 2. tixologiFees = (basePrice + customFees) × tixologiRoyalty% + tixologiFixed
 * 3. taxes = (basePrice + customFees + tixologiFees) × taxRate
 * 4. nuveiProcessing = (basePrice + tixologiFees) × stripeFee1 + stripeFee2
 * 5. finalPrice = basePrice + customFees + tixologiFees + taxes + nuveiProcessing
 */
export const calculateBasePrice = ({
	finalPrice,
	taxesData,
	event,
}: {
	finalPrice: number;
	taxesData: any;
	event: EventData | undefined;
}) => {
	if (!taxesData || !event || finalPrice <= 0) return null;
	const { ticketId } = useParams<TicketDetailsParams>();

	const { taxes } = taxesData;

	// Get tixologi royalty rates (percentage and fixed)
	const tixologiRoyalty =
		taxes?.ticketTaxes[ticketId]?.customTixologiRoyalty !== null
			? taxes?.ticketTaxes[ticketId]?.customTixologiRoyalty
			: taxes?.partnerRoyalties?.tixologiPrimaryRoyalty;
	const tixologiFixedRoyalty =
		taxes?.ticketTaxes[ticketId]?.tixologiFixedPrimaryRoyalty !== null
			? taxes?.ticketTaxes[ticketId]?.tixologiFixedPrimaryRoyalty
			: taxes?.partnerRoyalties?.tixologiFixedPrimaryRoyalty;

	// Get tax rate
	const taxRate = taxes?.ticketTaxes[ticketId]?.taxRate || 0;

	// Calculate custom fee rates
	// Separate flat fees from percentage fees
	let customFeeFlatTotal = 0;
	let customFeePercentageRate = 0;

	event.customFees.forEach((customFee) => {
		if (customFee.taxable) {
			if (customFee.type === 'flat') {
				customFeeFlatTotal += customFee.amount;
			} else {
				customFeePercentageRate += customFee.amount / 100;
			}
		}
	});

	// Convert percentages to decimals
	const r = tixologiRoyalty / 100; // tixologi royalty rate
	const t = taxRate; // tax rate
	const c = customFeePercentageRate; // custom fee percentage rate
	const f = tixologiFixedRoyalty; // tixologi fixed fee
	const cFlat = customFeeFlatTotal; // flat custom fees
	const s1 = stripeFee1; // stripe percentage (0.0294)
	const s2 = stripeFee2; // stripe flat fee (0.3) - matches checkout calculation

	/**
	 * Algebraic derivation:
	 * Let P = basePrice (what we're solving for)
	 *
	 * customFees = P × c + cFlat
	 * tixologiFees = (P + P×c + cFlat) × r + f = P×r + P×c×r + cFlat×r + f
	 * taxes = (P + P×c + cFlat + P×r + P×c×r + cFlat×r + f) × t
	 *       = P×t + P×c×t + cFlat×t + P×r×t + P×c×r×t + cFlat×r×t + f×t
	 * nuveiProcessing = (P + P×c + cFlat + P×r + P×c×r + cFlat×r + f + P×t + P×c×t + cFlat×t + P×r×t + P×c×r×t + cFlat×r×t + f×t) × s1 + s2
	 *                 = P×s1 + P×c×s1 + cFlat×s1 + P×r×s1 + P×c×r×s1 + cFlat×r×s1 + f×s1
	 *                   + P×t×s1 + P×c×t×s1 + cFlat×t×s1 + P×r×t×s1 + P×c×r×t×s1 + cFlat×r×t×s1 + f×t×s1 + s2
	 *
	 * finalPrice = P + P×c + cFlat + P×r + P×c×r + cFlat×r + f
	 *            + P×t + P×c×t + cFlat×t + P×r×t + P×c×r×t + cFlat×r×t + f×t
	 *            + P×s1 + P×c×s1 + cFlat×s1 + P×r×s1 + P×c×r×s1 + cFlat×r×s1 + f×s1
	 *            + P×t×s1 + P×c×t×s1 + cFlat×t×s1 + P×r×t×s1 + P×c×r×t×s1 + cFlat×r×t×s1 + f×t×s1 + s2
	 *
	 * Group P terms:
	 * finalPrice = P × (1 + c + r + c×r + t + c×t + r×t + c×r×t + s1 + c×s1 + r×s1 + c×r×s1 + t×s1 + c×t×s1 + r×t×s1 + c×r×t×s1)
	 *            + cFlat + cFlat×r + f + cFlat×t + cFlat×r×t + f×t + cFlat×s1 + cFlat×r×s1 + f×s1 + cFlat×t×s1 + cFlat×r×t×s1 + f×t×s1 + s2
	 *
	 * Solving for P:
	 * P = (finalPrice - constantTerms) / coefficientOfP
	 */

	// Calculate the coefficient of P (all terms that multiply P)
	const coefficientOfP =
		1 +
		c +
		r +
		c * r +
		t +
		c * t +
		r * t +
		c * r * t +
		s1 +
		c * s1 +
		r * s1 +
		c * r * s1 +
		t * s1 +
		c * t * s1 +
		r * t * s1 +
		c * r * t * s1;

	// Calculate all constant terms (terms without P)
	const constantTerms =
		cFlat +
		cFlat * r +
		f +
		cFlat * t +
		cFlat * r * t +
		f * t +
		cFlat * s1 +
		cFlat * r * s1 +
		f * s1 +
		cFlat * t * s1 +
		cFlat * r * t * s1 +
		f * t * s1 +
		s2;

	// Solve for base price
	const basePrice = (finalPrice - constantTerms) / coefficientOfP;

	// Validate result
	if (basePrice <= 0) {
		return null;
	}

	return round(basePrice);
};

export const calculateFinalPrice = ({
	initialPrice,
	taxesData,
	event,
}: {
	initialPrice: number;
	taxesData: any;
	event: EventData | undefined;
}) => {
	if (!taxesData || !event || initialPrice <= 0) return null;
	const { ticketId } = useParams<TicketDetailsParams>();
	const customFees = handleCustomFees(event, initialPrice);
	const taxableCustomFees = handleTaxableCustomFees(event, initialPrice);

	const { taxes } = taxesData;

	const tixologiRoyalty =
		taxes?.ticketTaxes[ticketId]?.customTixologiRoyalty !== null
			? taxes?.ticketTaxes[ticketId]?.customTixologiRoyalty
			: taxes?.partnerRoyalties?.tixologiPrimaryRoyalty;
	const tixologiFixedRoyalty =
		taxes?.ticketTaxes[ticketId]?.tixologiFixedPrimaryRoyalty !== null
			? taxes?.ticketTaxes[ticketId]?.tixologiFixedPrimaryRoyalty
			: taxes?.partnerRoyalties?.tixologiFixedPrimaryRoyalty;

	const fees = round((initialPrice + customFees) * (tixologiRoyalty / 100) + tixologiFixedRoyalty);

	const myTaxes = round((initialPrice + taxableCustomFees + fees) * taxes?.ticketTaxes[ticketId]?.taxRate);

	const currentNuveiFees = (initialPrice + fees + customFees + myTaxes) * stripeFee1 + stripeFee2;

	const finalPrice = initialPrice + fees + customFees + myTaxes + currentNuveiFees;

	return round(finalPrice);
};

const handleCustomFees = (event: EventData, ticketPrice: number) => {
	let customFees = 0;
	event.customFees.forEach((customFee) => {
		if (customFee.type === 'flat') {
			customFees += customFee.amount;
		} else {
			customFees += ticketPrice * (customFee.amount / 100);
		}
	});
	return customFees;
};

const handleTaxableCustomFees = (event: EventData, ticketPrice: number) => {
	let customFees = 0;
	event.customFees.forEach((customFee) => {
		if (customFee.taxable === 1) {
			if (customFee.type === 'flat') {
				customFees += customFee.amount;
			} else {
				customFees += ticketPrice * (customFee.amount / 100);
			}
		}
	});
	return customFees;
};

export const getEventCustomFees = (event: EventData, excludeTaxable = false) =>
	event.ticketTypes.reduce((acc, { quantity, initialPrice }) => {
		const fee = event.customFees.reduce((customFees, { type, amount, taxable }) => {
			if (!taxable && !excludeTaxable) {
				return 0;
			}

			if (type === 'flat') {
				return customFees + round(quantity * amount);
			} else {
				return customFees + round(quantity * initialPrice * (amount / 100));
			}
		}, 0);

		return [...acc, fee];
	}, [] as number[]);
